import { useCallback, useEffect, useState } from 'react';
import { BillProvider } from './bill.tsx';
import { StoreProvider, selectedBranchId, useSession, useStore } from './store.tsx';
import { ToastHost, useToast } from './ui.tsx';
import { BranchPickScreen } from './screens/BranchPickScreen.tsx';
import { DrawerScreen } from './screens/DrawerScreen.tsx';
import { LoginScreen } from './screens/LoginScreen.tsx';
import { OpenDrawerScreen } from './screens/OpenDrawerScreen.tsx';
import { PosShell } from './screens/PosShell.tsx';
import { RefundScreen } from './screens/RefundScreen.tsx';

/**
 * ★ หน้าขายเป็นหน้าเดียว ★ สลับเนื้อด้วยแถบหมวดล่าง — โครงเดียวกับแอปเดิม
 *
 * ที่แยกออกมาเป็นหน้าจริง ๆ มีแค่สามอย่างที่ของเดิมก็แยกเหมือนกัน
 *   ชำระเงิน · คืนเงิน · ลิ้นชัก
 */
type View =
  | { name: 'pos' }
  | { name: 'refund'; saleId: number }
  | { name: 'drawer' };

export function App() {
  return (
    <ToastHost>
      <Root />
    </ToastHost>
  );
}

function Root() {
  const session = useSession();
  const toast = useToast();
  const [branchId, setBranchId] = useState<number | null>(null);

  useEffect(() => {
    setBranchId(session ? selectedBranchId(session) : null);
  }, [session]);

  const onFailure = useCallback((message: string) => toast(message, 'crit'), [toast]);

  if (!session) return <LoginScreen />;
  if (branchId === null) return <BranchPickScreen onPicked={setBranchId} />;

  return (
    <StoreProvider session={session} branchId={branchId} onFailure={onFailure}>
      <BillProvider>
        <Screens />
      </BillProvider>
    </StoreProvider>
  );
}

function Screens() {
  const store = useStore();
  const [view, setView] = useState<View>({ name: 'pos' });

  // ★ ไม่มีรอบลิ้นชัก = ขายไม่ได้ ★ ทุกบิลต้องผูกกับรอบเสมอ
  if (!store.drawer) {
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <OpenDrawerScreen />
      </div>
    );
  }

  if (view.name === 'refund') {
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <RefundScreen
          saleId={view.saleId}
          onDone={() => setView({ name: 'pos' })}
          onBack={() => setView({ name: 'pos' })}
        />
      </div>
    );
  }

  if (view.name === 'drawer') {
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <DrawerScreen onClosed={() => setView({ name: 'pos' })} />
      </div>
    );
  }

  return (
    <PosShell onDrawer={() => setView({ name: 'drawer' })} />
  );
}
